<script setup lang="ts">
import { computed } from 'vue';
// biome-ignore lint/correctness/noUnusedImports: used via <ClassicPortraitGalleryCounter> in <template>
import ClassicPortraitGalleryCounter from './ClassicPortraitGalleryCounter.vue';
import type { MixedPortraitGalleryItem } from './classic-mixed-portrait-gallery';
import { useMixedItemView } from './useMixedItemView';

// How the tile takes its size: `height` from --cmg-tile-max-height (and,
// when set, --cmg-tile-max-width) declared by the engine around it, or
// `width` from the grid column it sits in.
type TileSizing = 'height' | 'width';

interface Props {
  active: boolean;
  count: number | undefined;
  index: number;
  item: MixedPortraitGalleryItem;
  selected: boolean;
  selectionOnTile: boolean;
  sizing?: TileSizing;
}

const props = withDefaults(defineProps<Props>(), { sizing: 'height' });
const emit = defineEmits<{
  activate: [event: MouseEvent];
  countDecrement: [event: MouseEvent];
  countIncrement: [event: MouseEvent];
  itemLoad: [event: Event];
  tryAgain: [];
}>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const { ratio, orientation, canRetry, ready, copyKey } = useMixedItemView(
  () => props.item
);
// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const pending = computed(() => !(props.item.isPreloaded || props.item.isError));

function activate(event: MouseEvent): void {
  if (!props.item.isError) {
    emit('activate', event);
  }
}

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
function activateFromKeyboard(event: KeyboardEvent): void {
  if (
    event.target !== event.currentTarget ||
    (event.key !== 'Enter' && event.key !== ' ')
  ) {
    return;
  }
  event.preventDefault();
  activate(new MouseEvent('click'));
}
</script>

<template>
  <div
    class="ClassicMixedPortraitGallery__thumbnail"
    :class="[
      `ClassicMixedPortraitGallery__thumbnail--${orientation}`,
      `ClassicMixedPortraitGallery__thumbnail--${sizing}`,
      {
        'ClassicMixedPortraitGallery__thumbnail--active': active,
        'ClassicMixedPortraitGallery__thumbnail--selected': selected,
        'ClassicMixedPortraitGallery__thumbnail--pending': pending,
        'ClassicMixedPortraitGallery__thumbnail--error': item.isError,
        'Portraits__item--loading': pending,
      },
    ]"
    :style="{ '--ClassicMixedPortraitGallery--item-ratio': String(ratio) }"
    :role="item.isError ? undefined : 'button'"
    :tabindex="ready ? 0 : -1"
    :aria-label="`${$t(copyKey('title'))} ${index + 1}`"
    :aria-current="active ? 'true' : undefined"
    :aria-disabled="!ready"
    :aria-pressed="selectionOnTile ? selected : undefined"
    :data-gallery-thumbnail="item.uuid"
    data-testid="gallery-item"
    :data-kind="item.kind"
    :data-loading="pending ? 'true' : 'false'"
    :data-error="item.isError ? 'true' : 'false'"
    :data-orientation="orientation"
    :data-selected="selected ? 'true' : 'false'"
    :data-uuid="item.uuid"
    @click="activate"
    @keydown="activateFromKeyboard"
  >
    <img
      v-if="ready"
      class="sentry-block"
      :src="item.url ?? undefined"
      :alt="`${$t(copyKey('title'))} ${index + 1}`"
      draggable="false"
      @load="emit('itemLoad', $event)"
    />
    <button
      v-else-if="item.isError"
      class="ClassicMixedPortraitGallery__thumbnail-error kiosk-button"
      type="button"
      :disabled="!canRetry"
      :aria-label="$t(copyKey('buttonRetry'))"
      data-testid="portraits-error-retry"
      @click="emit('tryAgain')"
    >
      {{ item.isRetrying ? '…' : '!' }}
    </button>
    <i v-else class="ClassicMixedPortraitGallery__thumbnail-spinner kiosk-spinner" />
    <slot name="status" :pending="pending" />
    <span
      v-if="selected"
      class="ClassicMixedPortraitGallery__thumbnail-mark"
      aria-hidden="true"
    >
      ✓
    </span>
    <ClassicPortraitGalleryCounter
      v-if="selectionOnTile && selected"
      compact
      :count="count"
      @decrement="emit('countDecrement', $event)"
      @increment="emit('countIncrement', $event)"
    />
  </div>
</template>

<style scoped lang="scss">
/* The tile is as tall as the engine lets it and as wide as its media ratio
   needs: --cmg-tile-max-height is the row height (rows) or the zone height
   (zones), --cmg-tile-max-width caps it so the next tile peeks in. Both
   are read here, on the tile, so their cqw/cqh resolve against the frame
   around it. A feed tile fills its column instead. */
.ClassicMixedPortraitGallery__thumbnail {
  --cmg-tile-fit-height: min(
    var(--cmg-tile-max-height, 99999px),
    calc(
      var(--cmg-tile-max-width, 99999px) /
        var(--ClassicMixedPortraitGallery--item-ratio)
    )
  );

  box-sizing: border-box;
  position: relative;
  flex: 0 0 auto;
  width: calc(
    var(--cmg-tile-fit-height) * var(--ClassicMixedPortraitGallery--item-ratio)
  );
  height: var(--cmg-tile-fit-height);
  padding: 0;
  overflow: hidden;
  color: var(--Kiosk--font_color);
  cursor: pointer;
  user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
  background: rgba(var(--Kiosk--tint-color-rgb), 0.08);
  border: 1px solid rgba(var(--Kiosk--tint-color-rgb), 0.22);
  border-radius: var(--cmg-radius, 0.8rem);
  outline: none;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;
}

.ClassicMixedPortraitGallery__thumbnail--width {
  width: 100%;
  min-width: 0;
  height: auto;
  aspect-ratio: var(--ClassicMixedPortraitGallery--item-ratio);
}

.ClassicMixedPortraitGallery__thumbnail--active,
.ClassicMixedPortraitGallery__thumbnail--selected,
.ClassicMixedPortraitGallery__thumbnail:focus-visible {
  border-color: var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__thumbnail--active,
.ClassicMixedPortraitGallery__thumbnail:focus-visible {
  box-shadow: inset 0 0 0 0.22rem var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__thumbnail--selected {
  box-shadow: inset 0 0 0 0.28rem var(--Kiosk--button_background_color);
}

.ClassicMixedPortraitGallery__thumbnail--pending,
.ClassicMixedPortraitGallery__thumbnail--error {
  cursor: default;
}

.ClassicMixedPortraitGallery__thumbnail--pending {
  background: rgba(15, 15, 15, 0.55);
}

.ClassicMixedPortraitGallery__thumbnail img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.ClassicMixedPortraitGallery__thumbnail-spinner,
.ClassicMixedPortraitGallery__thumbnail-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.ClassicMixedPortraitGallery__thumbnail-spinner {
  --kiosk-spinner-size: 1.8rem;
  --kiosk-spinner-color: #ffffff;
}

.ClassicMixedPortraitGallery__thumbnail-error {
  --kiosk-button-width: var(--cmg-touch);
  --kiosk-button-min-width: var(--cmg-touch);
  --kiosk-button-height: var(--cmg-touch);
  --kiosk-button-min-height: var(--cmg-touch);
  --kiosk-button-padding: 0;
  --kiosk-button-font-size: 1.5rem;
}

.ClassicMixedPortraitGallery__thumbnail-mark {
  position: absolute;
  top: 0.45rem;
  right: 0.45rem;
  z-index: 3;
  display: grid;
  place-items: center;
  width: 1.8rem;
  height: 1.8rem;
  font-size: 1rem;
  color: var(--Kiosk--button_font_color);
  background: var(--Kiosk--button_background_color);
  border-radius: 100%;
}
</style>
